/**
 * A marca da Plantus — o símbolo e o nome.
 *
 * <h2>O símbolo é o oficial; o VETOR é uma reprodução</h2>
 *
 * O desenho deixou de ser um monograma inventado: é a espiral com folhas da
 * marca da Plantus, traçada a partir do raster que o Product Owner enviou.
 * <b>Não existe arquivo vetorial oficial</b> — o traço é uma reprodução
 * técnica, com fidelidade medida (IoU 0,98438 contra o raster de origem), e
 * deve ser substituída pelo vetor oficial no dia em que ele existir. O
 * caminho inteiro é gerado por {@code marca/vetorizar.py}; ver
 * {@code marca/README.md} e {@link SILHUETA}.
 *
 * <h2>O nome ainda é TEXTO, e isso é uma diferença de verdade</h2>
 *
 * A logo horizontal do PO escreve "Plantus" numa cursiva desenhada, com a
 * assinatura <i>innovation is our nature</i> embaixo. Aqui o nome sai na fonte
 * do sistema, em caixa alta. Não é descuido: vetorizar uma cursiva a partir de
 * 179 px de largura produz letra tremida, e letra tremida num cabeçalho é pior
 * que letra correta na fonte errada. A logo horizontal fica em
 * {@code marca/original/} esperando o vetor oficial.
 *
 * <h2>A marca também vai para o papel</h2>
 *
 * A mesma marca imprime na <b>etiqueta de lote</b>, que tem 62 mm de largura,
 * sai numa impressora de <b>uma tinta só</b> e vira documento de chão de
 * fábrica. Duas regras seguem valendo, e uma foi <b>perdida na troca</b>:
 *
 * <ul>
 *   <li><b>Uma cor e um caminho.</b> O símbolo é uma silhueta única com
 *       {@code fill-rule="evenodd"}: o vazio de dentro de cada espiral é
 *       <b>buraco</b>, não uma segunda cor por cima. Num papel de uma tinta,
 *       contraforma pintada de branco desaparece — e o que sobra é uma
 *       mancha.</li>
 *   <li><b>Sem placa no papel.</b> A variante {@code monocromatica} não desenha
 *       o fundo verde. Placa escura em térmica é toner à toa e um retângulo
 *       preto grande o suficiente para competir com o código de barras.</li>
 *   <li>🔴 <b>Traço grosso — esta caiu.</b> O monograma provisório tinha 3,6
 *       de 32 unidades no ponto mais fino (11 % da altura) porque foi desenhado
 *       para isso. A marca de verdade é um floreio de linha fina: a espessura
 *       <b>mediana</b> é 3,64 % da altura e o percentil 5 é 1,14 %. A 7,4 mm
 *       de altura no papel isso dá 0,27 mm de traço mediano e 0,08 mm nas
 *       pontas — abaixo de um ponto de uma térmica de 203 dpi. Em laser de
 *       600 dpi (a "impressora comum" da etiqueta) o traço mediano tem 6,5
 *       pontos e sai; as pontas das folhas engrossam ou somem.
 *       <br>Isto <b>não se conserta em código</b>: é o desenho. Está anotado
 *       aqui e em {@code marca/README.md} porque a decisão — aceitar, ou pedir
 *       uma versão simplificada para tamanho pequeno — é do PO com a folha na
 *       mão.</li>
 * </ul>
 *
 * <p>E é por isso que a variante monocromática não é um "modo de impressão"
 * escondido: ela é uma variante nomeada, que a tela de etiqueta pede
 * explicitamente. O {@code @media print} de {@code ui.css} é a rede embaixo —
 * qualquer marca que chegue ao papel por outro caminho também cai para uma
 * tinta só.
 */

import type { ReactNode } from "react";

import { CAIXA, LADO, RAIO_DA_PLACA, SILHUETA } from "./marcaSimbolo";

import "./Marca.css";

/* ═══════════════════════════════════════════════════════════════════════
   PONTO DE TROCA DO LOGOTIPO
   ───────────────────────────────────────────────────────────────────────
   TUDO o que muda quando o logotipo oficial chegar está neste objeto e no
   módulo que ele importa. Nenhuma tela, nenhum componente, nenhuma folha
   de estilo.

   O desenho mora em `./marcaSimbolo.ts` porque ele é GERADO — 18 kB de
   curva de Bézier que ninguém transcreve à mão sem entortar. Trocar o
   logotipo é rodar `python3 marca/vetorizar.py` com o arquivo novo em
   `marca/original/`.

   O que o desenho novo precisa respeitar para continuar servindo à
   etiqueta impressa:

     · UM caminho só, em `silhueta`, com contraformas como subcaminhos
       (`fill-rule="evenodd"` já está aplicado). Sem `fill` próprio: quem
       pinta é o CSS, e é isso que permite branco sobre o cabeçalho verde
       e preto sobre o papel com o MESMO desenho;
     · a `caixa` quadrada, porque a placa institucional é um quadrado de
       canto arredondado e um desenho retangular nadaria dentro dela;
     · espessura mínima do traço acima de ~3 % da altura, se um dia se
       quiser a marca abaixo de 28 px ou abaixo de 8 mm. O desenho atual
       NÃO cumpre isso — ver o cabeçalho deste arquivo.

   Se o logotipo oficial vier em mais de uma cor, ele precisa vir TAMBÉM
   com a versão de uma tinta. A etiqueta de lote não negocia isso.
   ═══════════════════════════════════════════════════════════════════════ */
const LOGOTIPO = {
  /** Sistema de coordenadas do desenho. Quadrado, por causa da placa. */
  caixa: CAIXA,

  /** Lado da caixa e raio da placa — a placa acompanha o desenho. */
  lado: LADO,
  raioDaPlaca: RAIO_DA_PLACA,

  /** A espiral com folhas, em um caminho só. */
  silhueta: SILHUETA,

  /** O nome como ele se escreve. Vira o rótulo acessível do símbolo sozinho. */
  nome: "PLANTUS",
} as const;
/* ═══════════════════ FIM DO PONTO DE TROCA ═══════════════════════════ */

/** Símbolo sozinho, ou símbolo com o nome ao lado. */
export type FormatoDaMarca = "completa" | "simbolo";

/**
 * Como a marca é pintada.
 *
 * <p>Os três existem porque o fundo atrás dela muda, e uma marca que só sabe
 * viver num fundo é uma marca que some no outro.
 */
export type VarianteDaMarca =
  /** Placa verde escura com o símbolo vazado — para fundo CLARO (login, cartão). */
  | "institucional"
  /** Sem placa, tudo na cor herdada — para dentro do cabeçalho, que JÁ é a placa. */
  | "sobreMarca"
  /** Sem placa, uma tinta só — para o papel: etiqueta de lote, 62 mm, preto sobre branco. */
  | "monocromatica";

export type TamanhoDaMarca = "pequena" | "media" | "grande";

interface Props {
  formato?: FormatoDaMarca;
  variante?: VarianteDaMarca;
  tamanho?: TamanhoDaMarca;
  /**
   * Linha de apoio abaixo do nome — o módulo, a empresa, o ambiente.
   *
   * <p>Ignorada quando o formato é {@code simbolo}: não há onde escrever.
   */
  apoio?: ReactNode;
}

const CLASSE_DA_VARIANTE: Record<VarianteDaMarca, string> = {
  institucional: "ui-marca--institucional",
  sobreMarca: "ui-marca--sobre-marca",
  monocromatica: "ui-marca--monocromatica",
};

export function Marca({
  formato = "completa",
  variante = "institucional",
  tamanho = "media",
  apoio,
}: Props) {
  const comNome = formato === "completa";

  const classes = [
    "ui-marca",
    CLASSE_DA_VARIANTE[variante],
    `ui-marca--${tamanho}`,
    comNome ? "" : "ui-marca--simbolo",
  ]
    .filter((c) => c !== "")
    .join(" ");

  return (
    <span className={classes}>
      <svg
        className="ui-marca__simbolo"
        viewBox={LOGOTIPO.caixa}
        // Com o nome escrito ao lado, o símbolo é desenho: anunciá-lo faria o
        // leitor de tela dizer "Plantus" duas vezes seguidas. Sozinho, ele É o
        // nome, e aí precisa carregá-lo.
        {...(comNome
          ? { "aria-hidden": true as const }
          : { role: "img", "aria-label": LOGOTIPO.nome })}
        focusable="false"
      >
        {/* A placa não existe na variante monocromática — no papel de uma
            tinta ela viraria um retângulo preto. */}
        {variante === "institucional" && (
          <rect
            className="ui-marca__placa"
            x="0"
            y="0"
            width={LOGOTIPO.lado}
            height={LOGOTIPO.lado}
            rx={LOGOTIPO.raioDaPlaca}
          />
        )}
        <path className="ui-marca__sinal" d={LOGOTIPO.silhueta} fillRule="evenodd" />
      </svg>

      {comNome && (
        <span className="ui-marca__texto">
          <span className="ui-marca__nome">{LOGOTIPO.nome}</span>
          {apoio !== undefined && <span className="ui-marca__apoio">{apoio}</span>}
        </span>
      )}
    </span>
  );
}
